import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ClientLayout from '../components/layouts/ClientLayout';
import AdminLayout from '../admin/layouts/AdminLayout';
import AdminThemeProvider from '../admin/theme/AdminThemeProvider';
import RequireAdmin from './RequireAdmin';
import RedirectIfAdmin from './RedirectIfAdmin';
import RequireCustomer from './RequireCustomer';
import PageLoadingFallback from './PageLoadingFallback';

const Home = lazy(() => import('../client/pages/Home'));
const About = lazy(() => import('../client/pages/About'));
const Contact = lazy(() => import('../client/pages/Contact'));
const Gallery = lazy(() => import('../client/pages/Gallery'));
const Booking = lazy(() => import('../client/pages/Booking'));
const Login = lazy(() => import('../client/pages/Login'));
const Register = lazy(() => import('../client/pages/Register'));
const VerifyEmail = lazy(() => import('../client/pages/VerifyEmail'));
const ForgotPassword = lazy(() => import('../client/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('../client/pages/ResetPassword'));
const MyBookings = lazy(() => import('../client/pages/MyBookings'));
const Membership = lazy(() => import('../client/pages/Membership'));
const MembershipPaymentPage = lazy(() => import('../client/pages/MembershipPayment'));
const MembershipPaymentReturnPage = lazy(() => import('../client/pages/MembershipPaymentReturn'));
const MembershipPaymentSimulatePage = lazy(() => import('../client/pages/MembershipPaymentSimulate'));
const PaymentPage = lazy(() => import('../client/pages/Payment'));
const PaymentReturnPage = lazy(() => import('../client/pages/PaymentReturn'));
const PaymentSimulatePage = lazy(() => import('../client/pages/PaymentSimulate'));
const Terms = lazy(() => import('../client/pages/Terms'));
const Privacy = lazy(() => import('../client/pages/Privacy'));

const AdminLogin = lazy(() => import('../admin/pages/Login'));
const AdminForgotPassword = lazy(() => import('../admin/pages/ForgotPassword'));
const AdminResetPassword = lazy(() => import('../admin/pages/ResetPassword'));
const AdminDashboard = lazy(() => import('../admin/pages/Dashboard'));
const AdminNotifications = lazy(() => import('../admin/pages/Notifications'));
const AdminBookings = lazy(() => import('../admin/pages/Bookings'));
const AdminAddBooking = lazy(() => import('../admin/pages/AddBooking'));
const AdminSlotCalendar = lazy(() => import('../admin/pages/SlotCalendar'));
const AdminPayments = lazy(() => import('../admin/pages/Payments'));
const AdminReports = lazy(() => import('../admin/pages/Reports'));
const AdminCourtSettings = lazy(() => import('../admin/pages/CourtSettings'));
const AdminMembershipSettings = lazy(() => import('../admin/pages/MembershipSettings'));
const AdminUsers = lazy(() => import('../admin/pages/Users'));
const AdminVenueShowcase = lazy(() => import('../admin/pages/VenueShowcase'));
const AdminGallery = lazy(() => import('../admin/pages/Gallery'));

export default function AppRoutes() {
  return (
    <Suspense fallback={<PageLoadingFallback />}>
      <Routes>
        <Route element={<ClientLayout />}>
          <Route index element={<Home />} />
          <Route path="about" element={<About />} />
          <Route path="contact" element={<Contact />} />
          <Route path="gallery" element={<Gallery />} />
          <Route path="terms" element={<Terms />} />
          <Route path="privacy" element={<Privacy />} />
          <Route path="booking" element={<Booking />} />
          <Route path="membership" element={<Membership />} />
          <Route
            path="membership/pay/:id"
            element={
              <RequireCustomer>
                <MembershipPaymentPage />
              </RequireCustomer>
            }
          />
          <Route path="membership/payment/return" element={<MembershipPaymentReturnPage />} />
          <Route path="membership/payment/simulate" element={<MembershipPaymentSimulatePage />} />
          <Route path="booking/pay/:id" element={<PaymentPage />} />
          <Route path="booking/payment/return" element={<PaymentReturnPage />} />
          <Route path="booking/payment/simulate" element={<PaymentSimulatePage />} />
          <Route path="login" element={<Login />} />
          <Route path="register" element={<Register />} />
          <Route path="verify-email" element={<VerifyEmail />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="reset-password" element={<ResetPassword />} />
          <Route
            path="my-bookings"
            element={
              <RequireCustomer>
                <MyBookings />
              </RequireCustomer>
            }
          />
        </Route>

        <Route
          path="/admin/login"
          element={
            <AdminThemeProvider>
              <RedirectIfAdmin>
                <AdminLogin />
              </RedirectIfAdmin>
            </AdminThemeProvider>
          }
        />
        <Route
          path="/admin/forgot-password"
          element={
            <AdminThemeProvider>
              <AdminForgotPassword />
            </AdminThemeProvider>
          }
        />
        <Route
          path="/admin/reset-password"
          element={
            <AdminThemeProvider>
              <AdminResetPassword />
            </AdminThemeProvider>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminThemeProvider>
              <RequireAdmin>
                <AdminLayout />
              </RequireAdmin>
            </AdminThemeProvider>
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="notifications" element={<AdminNotifications />} />
          <Route path="bookings" element={<AdminBookings />} />
          <Route path="bookings/add" element={<AdminAddBooking />} />
          <Route path="schedule" element={<AdminSlotCalendar />} />
          <Route path="payments" element={<AdminPayments />} />
          <Route path="reports" element={<AdminReports />} />
          <Route path="court-settings" element={<AdminCourtSettings />} />
          <Route path="membership-settings" element={<AdminMembershipSettings />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="venue-showcase" element={<AdminVenueShowcase />} />
          <Route path="gallery" element={<AdminGallery />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
